// ColorBlockLayer — the soft, scene-aware color blocks that fade in
// behind the HUD as the active scene changes. Each of the 7 scenes has
// its own block (a large blurred radial gradient) positioned in one of
// the four cardinal directions (or center). The active scene's block
// cross-fades up to its target opacity, the others fade to 0.
//
// Mounted between the WebGL canvas (z=0) and the HUD layer (z=100).
// The layer is decorative: pointer-events are disabled.

import { useEffect, useState } from 'react';
import { SCENES } from './planetSections';
import { useReducedMotion } from 'motion/react';

declare global {
  interface Window {
    __planetSceneIndex?: number;
  }
}

export function ColorBlockLayer() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [mode, setMode] = useState<'narrative' | 'exploration'>(
    typeof window !== 'undefined' && window.__planetMode === 'exploration'
      ? 'exploration'
      : 'narrative',
  );
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setActiveIdx(window.__planetSceneIndex ?? 0);
    let last = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setActiveIdx(cur);
      }
      const m =
        window.__planetMode === 'exploration' ? 'exploration' : 'narrative';
      setMode((prev) => (prev === m ? prev : m));
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      className="color-block-layer"
      aria-hidden="true"
      data-mode={mode}
      style={reducedMotion ? { transition: 'none' } : undefined}
    >
      {SCENES.map((scene, i) => {
        // In exploration mode, keep the most recent scene's block visible
        // — don't fade based on scene changes (which won't happen anyway
        // but the rule also covers the case where the user scrolls while
        // in exploration mode).
        const isActive = mode === 'exploration' ? i === activeIdx : i === activeIdx;
        // When reduced motion is on, skip the cross-fade: jump straight
        // to the target opacity and let the next scene overwrite it.
        const targetOpacity = isActive ? scene.colorBlockOpacity : 0;
        return (
          <div
            key={scene.id}
            className={[
              'color-block-layer__block',
              `color-block-layer__block--${scene.colorBlockPosition}`,
              isActive ? 'is-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              background: scene.colorBlockColor,
              opacity: targetOpacity,
              transition: reducedMotion ? 'none' : undefined,
              // Per-block CSS variable so the layer knows its scene color.
              ['--block-color' as string]: scene.colorBlockColor,
            }}
            data-scene={scene.key}
            data-active={isActive ? 'true' : 'false'}
          />
        );
      })}
    </div>
  );
}

export default ColorBlockLayer;
